import { ReactElement } from 'react';
import classNames from 'classnames';
import { isEmpty } from 'lodash';

import { icon_edit, icon_trash } from '@gn-audio/icons';
import { SFCCProductItem } from '@gn-audio/jss-core/basket/models/sfcc-product-item';

import { SiteFeature } from '../../../types/siteSettings';
import {
  Meta3dEngravingRenderingUuidAttribute,
  ProductEngravingAttribute,
} from '../../../utils/customizerHelpers';
import useFallbackTranslation from '../../../utils/hooks/useFallbackTranslation';
import { Button } from '../../Button/Button';
import { EngravingModal } from '../../Engraving/EngravingModal';
import { getEngravingPreviewImageUrl } from '../../Engraving/engravingPreviewHelper';
import { FeatureToggle } from '../../FeatureToggle/FeatureToggle';
import { JabraIcon } from '../../JabraIcon/JabraIcon';
import { useBasket } from '../BasketContext/useBasket';

import basketStyles from '../Basket.module.css';
import engravingStyles from './Engraving.module.css';

const Engraving = ({ product }: { product: SFCCProductItem }): ReactElement | null => {
  const { setEngravingMessage, groups } = useBasket();
  const { t } = useFallbackTranslation();

  const handleChangeEngraving = (engravingText?: string) => {
    if (!product.item_id) return;
    setEngravingMessage(product.item_id, product.quantity, engravingText ?? '');
  };

  const handleRemoveEngraving = () => {
    if (!product.item_id) return;
    setEngravingMessage(product.item_id, product.quantity, '');
  };

  const hasEngraving = !isEmpty(product.c_engravingMsg);

  const productGroup = groups.find((group) => group.sku === product.product_id);
  if (!productGroup) return null;

  const selectedGroupEngravingAttribute = productGroup?.attributes?.find(
    (attr) => attr.key === ProductEngravingAttribute
  );
  const isEngravingAllowed = Boolean(selectedGroupEngravingAttribute);
  if (!isEngravingAllowed) return null;
  const renderingUuid = productGroup?.attributes?.find(
    (attr) => attr.key === Meta3dEngravingRenderingUuidAttribute
  )?.value;
  const previewImage = getEngravingPreviewImageUrl(productGroup.groupId);

  return (
    <FeatureToggle featureName={SiteFeature.SfccBasketEngraving}>
      <div className={classNames(basketStyles.card, basketStyles.grey, engravingStyles.container)}>
        <div className="flex-0 space-between flex flex-row items-center">
          <div className="flex flex-1 flex-col">
            <span className={engravingStyles.engravingWord}>{t('cart.Engraving')}:</span>
            <span className={classNames(basketStyles.fat, engravingStyles.engravingText)}>
              {hasEngraving ? (
                <>
                  <span>&ldquo;</span>
                  {product.c_engravingMsg}
                  <span>&rdquo;</span>
                </>
              ) : (
                <span>{t('cart.EngravingEmptyMessage')}</span>
              )}
            </span>
          </div>

          <div className="flex-0 flex gap-4">
            <EngravingModal
              renderingUuid={renderingUuid}
              onChange={handleChangeEngraving}
              engravingText={product.c_engravingMsg}
              productName={product.product_name}
              previewImage={previewImage}
            >
              {hasEngraving ? (
                <Button
                  variant="tertiary"
                  size="m"
                  icon={<JabraIcon className="size-6" Icon={icon_edit} />}
                  iconPlacement="right"
                  aria-label={t('cart.EngravingEdit')}
                />
              ) : (
                <Button variant="tertiary" size="s" aria-label={t('cart.EngravingAdd')}>
                  <span className="text-[0.75rem]"> {t('cart.EngravingAdd')}</span>
                </Button>
              )}
            </EngravingModal>
            {hasEngraving && (
              <Button
                variant="tertiary"
                size="m"
                icon={<JabraIcon className="size-6" Icon={icon_trash} />}
                iconPlacement="right"
                onClick={handleRemoveEngraving}
                className={engravingStyles.button}
                aria-label={t('cart.EngravingRemove')}
              />
            )}
          </div>
        </div>
      </div>
    </FeatureToggle>
  );
};

export default Engraving;
